import {
  Table,
  type TableProps,
  Transfer,
  type TransferProps,
} from '@/components';
import { useTranslation } from 'react-i18next';

type FieldType = {
  id: string;
  name: string;
  disabled?: boolean;
};

type Props<T> = Omit<TransferProps<T>, 'pagination'> & {
  dataSource: T[];
  columns: TableProps<T>['columns'];
  tablePagination?: TableProps<T>['pagination'];
};

export const TransferTable = <T extends FieldType>({
  columns,
  tablePagination,
  locale,
  ...restProps
}: Props<T>) => {
  const { t } = useTranslation();
  return (
    <Transfer
      showSelectAll={false}
      locale={{
        searchPlaceholder: t('search'),
        ...locale,
      }}
      {...restProps}
    >
      {({
        filteredItems,
        onItemSelect,
        onItemSelectAll,
        selectedKeys: listSelectedKeys,
      }) => {
        const rowSelection: TableProps<T>['rowSelection'] = {
          onSelect({ id, disabled }, selected) {
            if (disabled) {
              return;
            }
            onItemSelect(id, selected);
          },
          onChange(_, selectRow, { type }) {
            if (type === 'single') {
              return;
            }
            onItemSelectAll(
              selectRow.flatMap((x) => (x.disabled ? [] : x.id)),
              'replace',
            );
          },
          selectedRowKeys: listSelectedKeys,
          selections: [
            Table.SELECTION_ALL,
            Table.SELECTION_NONE,
            Table.SELECTION_INVERT,
          ],
          getCheckboxProps: (record) => ({
            disabled: !!record?.disabled,
          }),
        };

        return (
          <Table
            enableSticky={false}
            locale={{
              selectNone: t('select-none'),
              selectInvert: t('select-invert'),
            }}
            rowSelection={rowSelection}
            columns={columns}
            dataSource={filteredItems}
            onRow={({ id, disabled }) => ({
              style: disabled ? { pointerEvents: 'none' } : undefined,
              onClick: () => {
                if (disabled) {
                  return;
                }
                onItemSelect(id, !listSelectedKeys.includes(id));
              },
            })}
            pagination={tablePagination}
          />
        );
      }}
    </Transfer>
  );
};
